import { useTranslation } from 'react-i18next';
import { Calendar, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { ContextualHint, DayQuickSelect, TimePresets } from '../components';
import { DayCheckboxGroup } from '../../DayCheckboxGroup';

interface FirstTimeScheduleStepProps {
  selectedDays: number[];
  onSelectedDaysChange: (days: number[]) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  endTime: string;
  onEndTimeChange: (time: string) => void;
}

export function FirstTimeScheduleStep({
  selectedDays,
  onSelectedDaysChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
}: FirstTimeScheduleStepProps) {
  const { t } = useTranslation('staff');

  const timeError = startTime >= endTime;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
          <Calendar className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('availability.wizard.schedule.title', 'Set Working Hours')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('availability.wizard.schedule.subtitle', 'When can customers book?')}
          </p>
        </div>
      </div>

      {/* Hint */}
      <ContextualHint variant="explanation">
        {t(
          'availability.wizard.hints.schedule',
          'This sets when customers can book appointments. You can always add more availability later.'
        )}
      </ContextualHint>

      {/* Day selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.schedule.whichDays', 'Which days do you work?')}
        </Label>
        <DayQuickSelect
          selectedDays={selectedDays}
          onDaysChange={onSelectedDaysChange}
        />
        <DayCheckboxGroup value={selectedDays} onChange={onSelectedDaysChange} />
      </div>

      {/* Time selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.schedule.whatHours', 'What are your working hours?')}
        </Label>

        {/* Time presets */}
        <TimePresets
          startTime={startTime}
          endTime={endTime}
          onTimeChange={(start, end) => {
            onStartTimeChange(start);
            onEndTimeChange(end);
          }}
        />

        {/* Manual time pickers */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.startTime', 'Start Time')}
            </Label>
            <TimePicker
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t('availability.dialog.endTime', 'End Time')}
            </Label>
            <TimePicker
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
            />
          </div>
        </div>

        {/* Time error */}
        {timeError && (
          <p className="text-sm text-red-600">
            {t('availability.errors.endTimeAfterStart', 'End time must be after start time')}
          </p>
        )}
      </div>
    </div>
  );
}
